"use client";

import React from "react";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import Input from "@/components/form/input/InputField";
import Radio from "@/components/form/input/Radio";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { PencilIcon, TrashBinIcon } from "@/icons";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

const statusOptions = [
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" },
];

export default function RmcGstMasters() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [rate, setRate] = React.useState("");
  const [remark, setRemark] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);

  const isActiveFilter = statusFilter === "active";
  const { data: rows = [], isFetching } = useQuery({
    queryKey: ["gstMasters", isActiveFilter],
    queryFn: async () =>
      (
        await rmcClient.listGstMasters({
          is_active: isActiveFilter,
        })
      ).data,
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setRate("");
    setRemark("");
    setIsActive(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setRate("");
    setRemark("");
    setIsActive(true);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setRate(String(row.rate ?? ""));
    setRemark(row.remark ?? "");
    setIsActive(Boolean(row.is_active));
    setModalOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const normalizedRate = String(rate).trim();
      const parsedRate = Number(normalizedRate);
      if (!Number.isFinite(parsedRate) || parsedRate < 0) {
        throw new Error("rate");
      }
      if (editingId != null) {
        return rmcClient.updateGstMaster(editingId, {
          rate: normalizedRate,
          remark: remark.trim(),
          is_active: isActive,
        });
      }
      return rmcClient.createGstMaster({
        rate: normalizedRate,
        remark: remark.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gstMasters"] });
      toast.success(editingId != null ? "GST Master updated" : "GST Master created");
      closeModal();
    },
    onError: (e) => {
      if (e.message === "rate") toast.error("Enter a valid GST rate");
      else toast.error(handleApiError(e));
    },
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteGstMaster(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gstMasters"] });
      toast.success("GST Master deleted");
      setDeleteRow(null);
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageBreadCrumb pageTitle="GST Master" />
        <Button size="sm" onClick={openCreate}>
          Add GST
        </Button>
      </div>

      <div className="flex items-center gap-2">
        {statusOptions.map((o) => (
          <Button
            key={o.id}
            size="sm"
            variant={statusFilter === o.id ? "primary" : "outline"}
            className="h-8 rounded-md px-3 py-1 text-xs shadow-none"
            onClick={() => setStatusFilter(o.id)}
          >
            {o.name}
          </Button>
        ))}
      </div>

      <DataTable
        columns={[
          {
            key: "rate",
            header: "GST Rate (%)",
            render: (r) => Number(r.rate || 0).toFixed(2),
          },
          {
            key: "remark",
            header: "Remark",
            render: (r) => r.remark || "—",
          },
          {
            key: "status",
            header: "Status",
            render: (r) => (r.is_active ? "Active" : "Inactive"),
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (row) => (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={`Edit GST ${row.id}`}
                  title="Edit"
                  disabled={save.isPending || remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(row);
                  }}
                >
                  <PencilIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete GST ${row.id}`}
                  title="Delete"
                  disabled={save.isPending || remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400 disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteRow(row);
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
        emptyMessage={
          isActiveFilter ? "No active GST records found." : "No inactive GST records found."
        }
      />

      <Modal isOpen={modalOpen} onClose={closeModal} className="max-w-md w-full">
        <h3 className="pr-10 text-base font-semibold text-white">
          {editingId != null ? "Edit GST Master" : "Add GST Master"}
        </h3>
        <div className="mt-4 space-y-3">
          <div>
            <Label>Rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Remark</Label>
            <Input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              className="mt-1"
              placeholder="Optional remark"
            />
          </div>
          {editingId != null && (
            <div>
              <Label>Status</Label>
              <div className="mt-2 flex items-center gap-6">
                <Radio
                  id="gst-status-active"
                  name="gst-status"
                  value="active"
                  label="Active"
                  checked={isActive}
                  onChange={() => setIsActive(true)}
                />
                <Radio
                  id="gst-status-inactive"
                  name="gst-status"
                  value="inactive"
                  label="Inactive"
                  checked={!isActive}
                  onChange={() => setIsActive(false)}
                />
              </div>
            </div>
          )}
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" onClick={closeModal} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving..." : editingId != null ? "Update" : "Save"}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={deleteRow != null}
        onClose={() => {
          if (!remove.isPending) setDeleteRow(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete GST Master?</h3>
          <p className="text-sm text-gray-400">
            This will permanently remove GST rate{" "}
            <span className="font-medium text-gray-200">
              {deleteRow?.rate != null ? Number(deleteRow.rate).toFixed(2) : ""}
            </span>
            .
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={remove.isPending}
              onClick={() => setDeleteRow(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={remove.isPending}
              onClick={() => deleteRow?.id && remove.mutate(deleteRow.id)}
            >
              {remove.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
